import { motion, useAnimate, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useState } from 'react';
import { create } from 'zustand';

import { Box } from '@bonusly/design-system';

import { Bizy } from './bizy';
import { useReduceBizyMotionPreference } from './bizy-motion-preferences';
import { BizyState } from './types';

export type BizyFlightAnchorName = 'chat' | 'navFallback' | 'giveBoxGauge';

type AnchorRegistry = Record<BizyFlightAnchorName, Record<string, HTMLElement>>;
type BizyFlightSourceName = BizyFlightAnchorName | 'offscreenRight';

type FlightPoint = {
	x: number;
	y: number;
};

type FlightRoute = 'toGiveBox' | 'toSource';
type FlightPhase = 'flying' | 'parked';

/** A one-shot gesture Bizy plays while parked, e.g. lunging to "tap" a target. */
type BizyFlightGesture = {
	/** Bumped each time so the same gesture replays. */
	id: number;
	kind: 'tap';
	/** Lunge offset (px) relative to Bizy's parked spot, toward the target. */
	dx: number;
	dy: number;
};

type BizyFlight = {
	id: number;
	route: FlightRoute;
	phase: FlightPhase;
	sourceAnchorName: BizyFlightSourceName;
	sourceAnchorId: string;
	destinationAnchorName: BizyFlightAnchorName;
	from: FlightPoint;
	to: FlightPoint;
	parkedState: BizyState;
	duration: number;
	gesture?: BizyFlightGesture;
	/** Overrides the in-flight facing animation; falls back to geometry when unset. */
	flyingState?: Extract<BizyState, 'flyingLeft' | 'flyingRight'>;
};

type BizyFlightStore = {
	anchors: AnchorRegistry;
	flight: BizyFlight | null;
	navSuppressionCount: number;
	registerAnchor: (name: BizyFlightAnchorName, id: string, element: HTMLElement | null) => void;
	suppressNavBizy: () => void;
	releaseNavBizy: () => void;
	flyToGiveBox: (parkedState?: BizyState, options?: { source?: 'preferred' | 'offscreenRight' }) => boolean;
	flyToTarget: (rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>, options?: FlyToTargetOptions) => boolean;
	flyBackToSource: () => boolean;
	/** Sends Bizy home to the side nav (chat panel, else nav icon), vanishing on arrival. */
	flyToNav: (parkedState?: BizyState) => boolean;
	setParkedState: (parkedState: BizyState) => void;
	/** Plays a one-shot gesture (e.g. a "tap" lunge) on the current parked flight. */
	playGesture: (gesture: Omit<BizyFlightGesture, 'id'>) => void;
	markParked: (id: number) => void;
	repositionParkedFlight: () => void;
	clearFlight: (id?: number) => void;
};

export type FlyToTargetSide = 'left' | 'right';

export type FlyToTargetOptions = {
	/** Which side of the target Bizy should land on. Defaults to 'right'. */
	offsetSide?: FlyToTargetSide;
	/** Gap in px between the target edge and Bizy. Defaults to BIZY_TARGET_GAP_PX. */
	gap?: number;
	/** Parked state once Bizy arrives. Defaults to pointing toward the target. */
	parkedState?: BizyState;
	/** Forces the in-flight facing animation instead of deriving it from geometry. */
	flyingState?: Extract<BizyState, 'flyingLeft' | 'flyingRight'>;
};

type ShouldStartBizyFlightOptions = {
	isBizyEnabled: boolean;
	isDraftAssistDesktop: boolean;
	reduceBizyMotion: boolean;
	shouldReduceMotion: boolean;
};

type ShouldStartBizyReducedMotionEntranceOptions = ShouldStartBizyFlightOptions & {
	isBizyPanelOpen: boolean;
};

const BIZY_FLIGHT_SIZE = 80;
const BIZY_TARGET_GAP_PX = 16;
const BIZY_FLIGHT_Z_INDEX = 2000;
const BIZY_FLIGHT_PEEK_Z_INDEX = 1450;
const BIZY_PEEK_CLIP_INSET_PX = 4;
const BIZY_PEEK_MASK_EDGE_OFFSET = 44;
/** Duration of a one-shot "tap" lunge (out and back). */
const BIZY_TAP_DURATION = 0.36;
let flightId = 0;
let gestureId = 0;

const createAnchorRegistry = (): AnchorRegistry => ({
	chat: {},
	navFallback: {},
	giveBoxGauge: {},
});

export const getFlightPoint = (rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>): FlightPoint => ({
	x: rect.left + rect.width / 2 - BIZY_FLIGHT_SIZE / 2,
	y: rect.top + rect.height / 2 - BIZY_FLIGHT_SIZE / 2,
});

const getViewportWidth = (): number => (typeof window === 'undefined' ? 0 : window.innerWidth);

const getNextFlightId = (): number => {
	flightId += 1;
	return flightId;
};

/**
 * Landing point for a tour hop: Bizy sits beside the target (not on top of it),
 * vertically centered on the target, offset to the requested side.
 */
export const getOffsetFlightPoint = (
	rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
	side: FlyToTargetSide,
	gap = BIZY_TARGET_GAP_PX
): FlightPoint => ({
	x: side === 'right' ? rect.left + rect.width + gap : rect.left - gap - BIZY_FLIGHT_SIZE,
	y: rect.top + rect.height / 2 - BIZY_FLIGHT_SIZE / 2,
});

export const getParkedPointingState = (side: FlyToTargetSide): Extract<BizyState, 'pointingLeft' | 'pointingRight'> =>
	side === 'right' ? 'pointingLeft' : 'pointingRight';

export const getOffscreenRightFlightPoint = (reference: FlightPoint, viewportWidth = getViewportWidth()): FlightPoint => ({
	x: viewportWidth + BIZY_FLIGHT_SIZE,
	y: reference.y,
});

export const getFlightDirection = (from: FlightPoint, to: FlightPoint): Extract<BizyState, 'flyingLeft' | 'flyingRight'> => {
	return to.x < from.x ? 'flyingLeft' : 'flyingRight';
};

/** Flight duration floor and ceiling, in seconds. */
const FLIGHT_MIN_DURATION = 0.55;
const FLIGHT_MAX_DURATION = 1.1;
/** Seconds per √px — sublinear, so long flights are proportionally quicker. Hops under ~450px hit the floor. */
const FLIGHT_DURATION_PER_SQRT_PX = 0.032;

export const getFlightDuration = (from: FlightPoint, to: FlightPoint): number => {
	const distance = Math.hypot(to.x - from.x, to.y - from.y);
	return Math.min(Math.max(Math.sqrt(distance) * FLIGHT_DURATION_PER_SQRT_PX, FLIGHT_MIN_DURATION), FLIGHT_MAX_DURATION);
};

/** Fraction of duration before arrival that Bizy swaps the flying pose for her resting pose, so the landing settles instead of snapping. */
const FLIGHT_SETTLE_LEAD_SHARE = 0.3;

/**
 * Control points for the S-curve: the first crests above the chord early, the second sits
 * below the target so the path dips then arrives straight up (the landing flare).
 */
export const getFlightControlPoints = (from: FlightPoint, to: FlightPoint): [FlightPoint, FlightPoint] => {
	const span = Math.hypot(to.x - from.x, to.y - from.y);
	const amplitude = Math.min(Math.max(span * 0.3, 45), 200);

	const dip = Math.min(Math.max(span * 0.2, 24), 70);
	const chordYAt = (fraction: number) => from.y + (to.y - from.y) * fraction;

	return [
		{ x: from.x + (to.x - from.x) * 0.15, y: chordYAt(0.15) - amplitude },
		{ x: to.x, y: to.y + dip },
	];
};

/** SVG `d` for the flight curve, fed to CSS `offset-path`. Arc-length based, so motion stays smooth and even. */
export const getFlightPathD = (from: FlightPoint, to: FlightPoint): string => {
	const [control1, control2] = getFlightControlPoints(from, to);

	return `M ${from.x} ${from.y} C ${control1.x} ${control1.y} ${control2.x} ${control2.y} ${to.x} ${to.y}`;
};

export const getGiveBoxPeekEntrancePoints = (restingPoint: FlightPoint) => ({
	peek: { x: restingPoint.x + BIZY_PEEK_MASK_EDGE_OFFSET - BIZY_PEEK_CLIP_INSET_PX, y: restingPoint.y + 4 },
	front: { x: restingPoint.x + 4, y: restingPoint.y + 2 },
	resting: restingPoint,
});

export const shouldStartBizyFlight = ({ isBizyEnabled, isDraftAssistDesktop, reduceBizyMotion, shouldReduceMotion }: ShouldStartBizyFlightOptions): boolean => {
	return isBizyEnabled && isDraftAssistDesktop && !reduceBizyMotion && !shouldReduceMotion;
};

export const shouldStartBizyReducedMotionEntrance = ({
	isBizyEnabled,
	isDraftAssistDesktop,
	isBizyPanelOpen,
	reduceBizyMotion,
	shouldReduceMotion,
}: ShouldStartBizyReducedMotionEntranceOptions): boolean => {
	return isBizyEnabled && isDraftAssistDesktop && !isBizyPanelOpen && (reduceBizyMotion || shouldReduceMotion);
};

export const shouldUseGiveBoxPeekEntrance = (flight: Pick<BizyFlight, 'route' | 'sourceAnchorName'> | null): boolean => {
	return flight?.route === 'toGiveBox' && flight.sourceAnchorName === 'offscreenRight';
};

const isUsableAnchor = (element: HTMLElement): boolean => {
	if (!element.isConnected) return false;
	const rect = element.getBoundingClientRect();
	if (rect.width <= 0 || rect.height <= 0) return false;

	let current: HTMLElement | null = element;
	while (current && current !== document.body) {
		const styles = window.getComputedStyle(current);
		if (styles.display === 'none' || styles.visibility === 'hidden' || styles.opacity === '0') return false;
		current = current.parentElement;
	}

	return true;
};

const getFirstUsableAnchor = (anchors: Record<string, HTMLElement>): HTMLElement | null => {
	return Object.values(anchors).find(isUsableAnchor) ?? null;
};

const getMeasurableAnchor = (anchors: Record<string, HTMLElement>): HTMLElement | null => {
	for (const element of Object.values(anchors)) {
		if (!element.isConnected) continue;
		const rect = element.getBoundingClientRect();
		if (rect.width > 0 && rect.height > 0) return element;
	}
	return null;
};

const getFirstUsableAnchorEntry = (anchors: Record<string, HTMLElement>): { id: string; element: HTMLElement } | null => {
	const entry = Object.entries(anchors).find(([, element]) => isUsableAnchor(element));
	if (!entry) return null;
	return { id: entry[0], element: entry[1] };
};

const getPreferredSourceAnchor = (anchors: AnchorRegistry): { name: BizyFlightAnchorName; id: string; element: HTMLElement } | null => {
	const chat = getFirstUsableAnchorEntry(anchors.chat);
	if (chat) return { name: 'chat', ...chat };

	const navFallback = getFirstUsableAnchorEntry(anchors.navFallback);
	if (navFallback) return { name: 'navFallback', ...navFallback };

	return null;
};

const getAnchorById = (anchors: AnchorRegistry, name: BizyFlightAnchorName, id: string): HTMLElement | null => {
	const element = anchors[name][id];
	if (!element?.isConnected) return null;
	return element;
};

const measureAnchor = (element: HTMLElement): FlightPoint => getFlightPoint(element.getBoundingClientRect());

const getReturnPoint = (flight: BizyFlight, anchors: AnchorRegistry, from: FlightPoint): FlightPoint | null => {
	if (flight.sourceAnchorName === 'offscreenRight') {
		return getOffscreenRightFlightPoint(from);
	}

	const source =
		getAnchorById(anchors, flight.sourceAnchorName, flight.sourceAnchorId) ?? getFirstUsableAnchor(anchors.chat) ?? getFirstUsableAnchor(anchors.navFallback);

	return source ? measureAnchor(source) : null;
};

export const useBizyFlightStore = create<BizyFlightStore>((set) => ({
	anchors: createAnchorRegistry(),
	flight: null,
	navSuppressionCount: 0,
	suppressNavBizy: () => set((state) => ({ navSuppressionCount: state.navSuppressionCount + 1 })),
	releaseNavBizy: () => set((state) => ({ navSuppressionCount: Math.max(0, state.navSuppressionCount - 1) })),
	registerAnchor: (name, id, element) =>
		set((state) => {
			const anchors = { ...state.anchors, [name]: { ...state.anchors[name] } };
			if (element) {
				anchors[name][id] = element;
			} else {
				delete anchors[name][id];
			}
			return { anchors };
		}),
	flyToGiveBox: (parkedState = 'curious', options = {}) => {
		let didStart = false;
		set((state) => {
			const destination = getFirstUsableAnchor(state.anchors.giveBoxGauge);
			if (!destination) return {};

			const to = measureAnchor(destination);
			const preferredSource = options.source === 'offscreenRight' ? null : getPreferredSourceAnchor(state.anchors);
			const source = preferredSource
				? {
						name: preferredSource.name,
						id: preferredSource.id,
						from: measureAnchor(preferredSource.element),
					}
				: {
						name: 'offscreenRight' as const,
						id: 'offscreenRight',
						from: getOffscreenRightFlightPoint(to),
					};
			didStart = true;
			return {
				flight: {
					id: getNextFlightId(),
					route: 'toGiveBox',
					phase: 'flying',
					sourceAnchorName: source.name,
					sourceAnchorId: source.id,
					destinationAnchorName: 'giveBoxGauge',
					from: source.from,
					to,
					parkedState,
					duration: getFlightDuration(source.from, to),
				},
			};
		});
		return didStart;
	},
	flyToTarget: (rect, options = {}) => {
		let didStart = false;
		set((state) => {
			const offsetSide = options.offsetSide ?? 'right';
			const to = getOffsetFlightPoint(rect, offsetSide, options.gap);
			const parkedState = options.parkedState ?? getParkedPointingState(offsetSide);
			// Chain from Bizy's current parked spot when a flight exists, otherwise enter from offscreen.
			const from = state.flight ? state.flight.to : getOffscreenRightFlightPoint(to);
			didStart = true;
			return {
				flight: {
					id: getNextFlightId(),
					route: 'toGiveBox',
					phase: 'flying',
					sourceAnchorName: state.flight?.sourceAnchorName ?? 'giveBoxGauge',
					sourceAnchorId: state.flight?.sourceAnchorId ?? 'giveBoxGauge',
					destinationAnchorName: 'giveBoxGauge',
					from,
					to,
					parkedState,
					duration: getFlightDuration(from, to),
					flyingState: options.flyingState,
				},
			};
		});
		return didStart;
	},
	flyBackToSource: () => {
		let didStart = false;
		set((state) => {
			const flight = state.flight;
			if (!flight) return {};

			const destination = getFirstUsableAnchor(state.anchors[flight.destinationAnchorName]);
			const from = destination ? measureAnchor(destination) : flight.to;
			const to = getReturnPoint(flight, state.anchors, from);
			if (!to) return { flight: null };

			didStart = true;
			return {
				flight: {
					...flight,
					id: getNextFlightId(),
					route: 'toSource',
					phase: 'flying',
					from,
					to,
					duration: getFlightDuration(from, to),
					// Settle into the side panel's resting pose so the handoff to the static
					// chat Bizy (which idles 'curious') is seamless rather than a flying-pose cut.
					parkedState: 'curious',
					// Derive facing from geometry for the return hop, ignoring any override.
					flyingState: undefined,
				},
			};
		});
		return didStart;
	},
	flyToNav: (parkedState = 'excited') => {
		let didStart = false;
		set((state) => {
			// Prefer the open chat panel's Bizy, else the nav icon — both live in the side nav.
			const nav = getPreferredSourceAnchor(state.anchors);
			if (!nav) return {};

			const to = measureAnchor(nav.element);
			// Chain from Bizy's current spot when a flight exists, otherwise enter from offscreen.
			const from = state.flight ? state.flight.to : getOffscreenRightFlightPoint(to);
			didStart = true;
			return {
				flight: {
					id: getNextFlightId(),
					// `toSource` so the overlay clears the flight on arrival — Bizy "lands"
					// in the nav and the nav icon takes over.
					route: 'toSource',
					phase: 'flying',
					sourceAnchorName: nav.name,
					sourceAnchorId: nav.id,
					destinationAnchorName: nav.name,
					from,
					to,
					parkedState,
					duration: getFlightDuration(from, to),
				},
			};
		});
		return didStart;
	},
	setParkedState: (parkedState) =>
		set((state) => {
			if (!state.flight) return {};
			return { flight: { ...state.flight, parkedState } };
		}),
	playGesture: (gesture) =>
		set((state) => {
			if (!state.flight) return {};
			gestureId += 1;
			return { flight: { ...state.flight, gesture: { ...gesture, id: gestureId } } };
		}),
	markParked: (id) =>
		set((state) => {
			if (!state.flight || state.flight.id !== id || state.flight.route !== 'toGiveBox' || state.flight.phase !== 'flying') return {};
			return { flight: { ...state.flight, phase: 'parked', from: state.flight.to } };
		}),
	repositionParkedFlight: () =>
		set((state) => {
			const flight = state.flight;
			if (!flight || flight.phase !== 'parked') return {};

			const destination = getMeasurableAnchor(state.anchors[flight.destinationAnchorName]);
			if (!destination) return {};

			const to = measureAnchor(destination);
			if (to.x === flight.to.x && to.y === flight.to.y) return {};

			return { flight: { ...flight, from: to, to } };
		}),
	clearFlight: (id) =>
		set((state) => {
			if (id != null && state.flight?.id !== id) return {};
			return { flight: null };
		}),
}));

export const useBizyFlightAnchor = (name: BizyFlightAnchorName) => {
	const id = useId();
	const registerAnchor = useBizyFlightStore((state) => state.registerAnchor);

	return useCallback(
		(element: HTMLElement | null) => {
			registerAnchor(name, id, element);
		},
		[id, name, registerAnchor]
	);
};

export const useIsBizyFlightAnchorHidden = (name: BizyFlightAnchorName): boolean => {
	return useBizyFlightStore((state) => {
		const flight = state.flight;
		if (name === 'navFallback') return !!flight || state.navSuppressionCount > 0;
		return !!flight && (flight.sourceAnchorName === name || flight.destinationAnchorName === name);
	});
};

export const useSuppressNavBizy = (isActive: boolean) => {
	const suppressNavBizy = useBizyFlightStore((state) => state.suppressNavBizy);
	const releaseNavBizy = useBizyFlightStore((state) => state.releaseNavBizy);

	useLayoutEffect(() => {
		if (!isActive) return;
		suppressNavBizy();
		return releaseNavBizy;
	}, [isActive, suppressNavBizy, releaseNavBizy]);
};

/**
 * The parked overlay owns Bizy's visible position, while the in-DOM anchor stays hidden.
 * Track the hidden-but-laid-out anchor so modal resizes do not leave the overlay behind.
 */
const findResizeTarget = (anchor: HTMLElement): HTMLElement => anchor.closest<HTMLElement>('.chakra-modal__content') ?? anchor;

const useTrackParkedFlightAnchor = (flight: BizyFlight | null) => {
	const isParked = flight?.phase === 'parked';
	const destinationAnchorName = flight?.destinationAnchorName;

	useEffect(() => {
		if (!isParked || !destinationAnchorName) return;

		let frameId: number | null = null;

		const reposition = () => {
			frameId = null;
			useBizyFlightStore.getState().repositionParkedFlight();
		};

		const scheduleReposition = () => {
			if (frameId !== null) return;
			frameId = window.requestAnimationFrame(reposition);
		};

		const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleReposition);
		let observedTarget: HTMLElement | null = null;

		const syncObservedTarget = () => {
			const anchor = getMeasurableAnchor(useBizyFlightStore.getState().anchors[destinationAnchorName]);
			const target = anchor ? findResizeTarget(anchor) : null;
			if (target === observedTarget) return;
			if (observedTarget) resizeObserver?.unobserve(observedTarget);
			if (target) resizeObserver?.observe(target);
			observedTarget = target;
		};

		syncObservedTarget();
		reposition();

		// Anchor refs can re-register if modal content remounts; keep observing the current box.
		const unsubscribe = useBizyFlightStore.subscribe(syncObservedTarget);
		const scrollOptions = { capture: true, passive: true };

		window.addEventListener('resize', scheduleReposition);
		window.addEventListener('scroll', scheduleReposition, scrollOptions);

		return () => {
			if (frameId !== null) window.cancelAnimationFrame(frameId);
			unsubscribe();
			resizeObserver?.disconnect();
			window.removeEventListener('resize', scheduleReposition);
			window.removeEventListener('scroll', scheduleReposition, scrollOptions);
		};
	}, [isParked, destinationAnchorName]);
};

export const BizyFlightOverlay = () => {
	const flight = useBizyFlightStore((state) => state.flight);
	const markParked = useBizyFlightStore((state) => state.markParked);
	const clearFlight = useBizyFlightStore((state) => state.clearFlight);
	const shouldReduceMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();

	useTrackParkedFlightAnchor(flight);

	if (!flight) return null;

	const shouldReduceBizyMotion = !!shouldReduceMotion || reduceBizyMotion;

	if (shouldUseGiveBoxPeekEntrance(flight)) {
		return <BizyGiveBoxPeekEntrance flight={flight} shouldReduceMotion={shouldReduceBizyMotion} markParked={markParked} />;
	}

	// No key on flight.id: keeping one instance mounted across the park → return handoff avoids
	// remounting Bizy (which would replay her mount fade-in — the translucent flash on return).
	return <BizyStandardFlight flight={flight} shouldReduceMotion={shouldReduceBizyMotion} markParked={markParked} clearFlight={clearFlight} />;
};

interface BizyStandardFlightProps {
	flight: BizyFlight;
	shouldReduceMotion: boolean;
	markParked: (id: number) => void;
	clearFlight: (id?: number) => void;
}

const BizyStandardFlight = ({ flight, shouldReduceMotion, markParked, clearFlight }: BizyStandardFlightProps) => {
	const isFlying = flight.phase === 'flying';
	const shouldAnimateFlight = isFlying && !shouldReduceMotion;
	// Switch from the flying pose to the resting pose just before arrival so the landing settles in instead of snapping.
	const [isSettlingIn, setIsSettlingIn] = useState(false);

	const [scope, animate] = useAnimate<HTMLDivElement>();

	const offsetPath = useMemo(() => `path("${getFlightPathD(flight.from, flight.to)}")`, [flight.from, flight.to]);

	// Layout effect so the reset lands before paint: a leg can start with isSettlingIn still true,
	// which would otherwise flash one frame of the resting pose. Swap to resting pose just before arrival.
	useLayoutEffect(() => {
		setIsSettlingIn(false);
		if (!shouldAnimateFlight) return;
		const settleAtMs = Math.max(flight.duration * (1 - FLIGHT_SETTLE_LEAD_SHARE) * 1000, 0);
		const timer = setTimeout(() => setIsSettlingIn(true), settleAtMs);
		return () => clearTimeout(timer);
	}, [flight.id, flight.duration, shouldAnimateFlight]);

	// Animate offset-distance imperatively so a new leg restarts from 0% without remounting (which would
	// replay Bizy's mount fade-in). Layout effect so the reset to 0% lands before paint: a leg swaps
	// offsetPath while the node still holds the prior leg's 100%, which on the new path would flash Bizy
	// at the destination.
	useLayoutEffect(() => {
		const node = scope.current;
		if (!node) return;

		const finish = () => (flight.route === 'toGiveBox' ? markParked(flight.id) : clearFlight(flight.id));

		if (!shouldAnimateFlight) {
			node.style.setProperty('offset-distance', '100%');
			finish();
			return;
		}

		const controls = animate(node, { offsetDistance: ['0%', '100%'] }, { duration: flight.duration, ease: 'easeInOut' });
		controls.then(finish).catch(() => undefined);
		return () => controls.stop();
		// Run once per leg; shouldAnimateFlight is excluded — it flips on park (same id) and must not restart.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flight.id]);

	const showFlyingPose = isFlying && !isSettlingIn;
	// Honor an explicit in-flight facing override (set by the demo's directed hops),
	// otherwise derive facing from geometry.
	const bizyState = showFlyingPose ? (flight.flyingState ?? getFlightDirection(flight.from, flight.to)) : flight.parkedState;
	// A parked tap lunge: a quick poke toward the target, then back to rest.
	const tap = !isFlying && !shouldReduceMotion ? flight.gesture : undefined;

	return (
		<Box position="fixed" inset={0} pointerEvents="none" zIndex={BIZY_FLIGHT_Z_INDEX}>
			<div
				ref={scope}
				// Ride the curve via offset-path: top-left tracks the path; offsetRotate fixed so Bizy doesn't bank into turns.
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: BIZY_FLIGHT_SIZE,
					height: BIZY_FLIGHT_SIZE,
					offsetPath,
					// Unmanaged while flying so the effect owns it; React only sets the resting 100%.
					offsetDistance: shouldAnimateFlight ? undefined : '100%',
					offsetAnchor: 'left top',
					offsetRotate: '0deg',
					willChange: 'offset-distance',
				}}
			>
				<motion.div
					key={tap?.id ?? 'rest'}
					animate={tap ? { x: [0, tap.dx, 0], y: [0, tap.dy, 0] } : { x: 0, y: 0 }}
					transition={tap ? { duration: BIZY_TAP_DURATION, ease: 'easeInOut', times: [0, 0.45, 1] } : { duration: 0 }}
					style={{ width: BIZY_FLIGHT_SIZE, height: BIZY_FLIGHT_SIZE }}
				>
					<Bizy
						state={bizyState}
						size={BIZY_FLIGHT_SIZE}
						// This overlay remounts when taking over from the peek entrance (give-box → nav return);
						// skip the mount fade so it doesn't flash — Bizy arrives via motion instead.
						skipEntranceFade
						// In flight, run only the wings — drop the idle loops that aren't visible mid-zoom, to keep frames steady.
						animations={
							showFlyingPose
								? { wingSpeed: 0.08, wingAmplitude: 0.8, wingMode: 'squishStretch', hoverBob: false, blink: false, antennaSway: false, eyeTracking: false }
								: undefined
						}
					/>
				</motion.div>
			</div>
		</Box>
	);
};

type PeekPhase = 'hidden' | 'peek' | 'wave' | 'front' | 'settle' | 'reducedMotion';

interface BizyGiveBoxPeekEntranceProps {
	flight: BizyFlight;
	shouldReduceMotion: boolean;
	markParked: (id: number) => void;
}

const getPeekEntranceAnimation = (phase: PeekPhase, points: ReturnType<typeof getGiveBoxPeekEntrancePoints>) => {
	switch (phase) {
		case 'hidden':
		case 'peek':
		case 'wave':
			return { x: points.peek.x, y: points.peek.y, opacity: 1, clipPath: `inset(0 0 0 ${BIZY_PEEK_CLIP_INSET_PX}px)` };
		case 'front':
			return { x: points.front.x, y: points.front.y, opacity: 1, clipPath: 'inset(0 0 0 0px)' };
		case 'settle':
			return { x: points.resting.x, y: points.resting.y, opacity: 1, clipPath: 'inset(0 0 0 0px)' };
		case 'reducedMotion':
			return { x: points.resting.x, y: points.resting.y, opacity: 1, clipPath: 'inset(0 0 0 0px)' };
	}
};

const getPeekEntranceBizyAnimation = (phase: PeekPhase) => {
	switch (phase) {
		case 'hidden':
			return { x: -BIZY_FLIGHT_SIZE, rotate: 0 };
		case 'peek':
		case 'wave':
			return { x: 0, rotate: -12 };
		case 'front':
		case 'settle':
		case 'reducedMotion':
			return { x: 0, rotate: 0 };
	}
};

export const getPeekEntranceBizyState = (phase: PeekPhase, parkedState: BizyState): BizyState => {
	if (phase === 'wave') return 'waving';
	if (phase === 'settle' || phase === 'reducedMotion') return parkedState;
	return 'curious';
};

const getPeekEntranceTransition = (phase: PeekPhase) => {
	switch (phase) {
		case 'peek':
			return { duration: 0.42, ease: 'linear' as const };
		case 'front':
			return { duration: 0.32, ease: 'linear' as const };
		case 'settle':
			return { duration: 0.46, ease: 'linear' as const };
		case 'reducedMotion':
			return { duration: 0.2, ease: 'linear' as const };
		case 'hidden':
		case 'wave':
			return { duration: 0 };
	}
};

const BizyGiveBoxPeekEntrance = ({ flight, shouldReduceMotion, markParked }: BizyGiveBoxPeekEntranceProps) => {
	const [phase, setPhase] = useState<PeekPhase>(shouldReduceMotion ? 'reducedMotion' : 'hidden');
	const points = getGiveBoxPeekEntrancePoints(flight.to);

	useEffect(() => {
		if (shouldReduceMotion) {
			setPhase('reducedMotion');
			const timer = setTimeout(() => markParked(flight.id), 200);
			return () => clearTimeout(timer);
		}

		setPhase('hidden');
		const timers = [
			setTimeout(() => setPhase('peek'), 16),
			setTimeout(() => setPhase('wave'), 440),
			setTimeout(() => setPhase('front'), 960),
			setTimeout(() => setPhase('settle'), 1280),
			setTimeout(() => markParked(flight.id), 1760),
		];

		return () => {
			timers.forEach(clearTimeout);
		};
	}, [flight.id, markParked, shouldReduceMotion]);

	return (
		<Box
			position="fixed"
			inset={0}
			pointerEvents="none"
			zIndex={phase === 'hidden' || phase === 'peek' || phase === 'wave' ? BIZY_FLIGHT_PEEK_Z_INDEX : BIZY_FLIGHT_Z_INDEX}
		>
			<motion.div
				key={flight.id}
				initial={shouldReduceMotion ? { ...getPeekEntranceAnimation('reducedMotion', points), opacity: 0 } : getPeekEntranceAnimation('hidden', points)}
				animate={getPeekEntranceAnimation(phase, points)}
				transition={getPeekEntranceTransition(phase)}
				style={{
					position: 'absolute',
					width: BIZY_FLIGHT_SIZE,
					height: BIZY_FLIGHT_SIZE,
					willChange: 'transform, opacity, clip-path',
				}}
			>
				<motion.div
					animate={getPeekEntranceBizyAnimation(phase)}
					transition={getPeekEntranceTransition(phase)}
					style={{ width: BIZY_FLIGHT_SIZE, height: BIZY_FLIGHT_SIZE, transformOrigin: 'left center' }}
				>
					<Bizy state={getPeekEntranceBizyState(phase, flight.parkedState)} size={BIZY_FLIGHT_SIZE} />
				</motion.div>
			</motion.div>
		</Box>
	);
};
